import React, { useEffect, useRef, useState } from "react";
import { Timer } from "lucide-react";

/**
 * Per-question countdown for Timed Interview Mode. Mount with a `key` that changes per
 * question (e.g. key={questionNumber}) so the timer fully resets on each new question.
 * Uses a ref for `onExpire` so the interval always calls the latest callback (avoids
 * stale-closure bugs from state captured at mount time).
 */
export default function QuestionTimer({ seconds, onExpire }) {
  const [remaining, setRemaining] = useState(seconds);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  useEffect(() => {
    if (!seconds) return;
    setRemaining(seconds);
    const interval = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(interval);
          onExpireRef.current();
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seconds]);

  if (!seconds) return null;
  const mm = Math.floor(remaining / 60).toString().padStart(2, "0");
  const ss = (remaining % 60).toString().padStart(2, "0");
  const urgent = remaining <= 20;

  return (
    <div
      data-testid="question-timer"
      className={`flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full transition-colors ${
        urgent ? "bg-rose-500/15 text-rose-300 animate-pulse" : "bg-white/5 text-slate-300"
      }`}
    >
      <Timer className="w-3.5 h-3.5" /> {mm}:{ss}
    </div>
  );
}
